import React from "react";
import { ListEditor } from "@/components/pageSettings/ListEditor";
import { INPUT } from "@/components/pageSettings/AdminModal";

const HINTS = {
  hero: "Use {firm} to insert the firm's name. Leave blank for the standard wording.",
  next_steps: "Leave the steps empty to use each page's own \u201cwhat happens next\u201d steps.",
  firm_contact: "Shows the firm's logo, name and phone, plus the company email, address and hours from each buyer's details. Never a person's name.",
  video: "Each buyer can add their own video link in their thank-you settings \u2014 theirs is used first. Hidden when there's no video.",
  testimonials: "Shows the buyer's approved client reviews. Hidden when they have none.",
  no_fee: "Leave the points empty to use the shared wording from Shared sections.",
  articles: "Leave empty to use the shared articles from Shared sections. Hidden when there are none.",
};
const STEP_FIELDS = [
  { k: "title", placeholder: "Step title", max: 80 }, { k: "body", placeholder: "Short description", max: 240 },
  { k: "image_url", placeholder: "Photo link (optional) — or upload one", max: 500, wide: true, upload: true },
];
const ARTICLE_FIELDS = [
  { k: "title", placeholder: "Article title", max: 120 }, { k: "url", placeholder: "Link (https://\u2026)", max: 500 },
  { k: "image_url", placeholder: "Image link (optional) — or upload one", max: 500, wide: true, upload: true },
];

const Text = ({ label, k, b, set, max, area, testId }) => (
  <label className="label-mono mt-2 block">{label}
    {area ? (
      <textarea className={`${INPUT} h-20 resize-none`} maxLength={max} value={b[k] || ""}
                onChange={(e) => set(k, e.target.value)} data-testid={`${testId}-${k}`} />
    ) : (
      <input className={INPUT} maxLength={max} value={b[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`${testId}-${k}`} />
    )}
  </label>
);

export function BlockFields({ b, onChange, testId }) {
  const set = (k, v) => onChange({ ...b, [k]: v });
  const p = { b, set, testId };
  return (
    <div className="border-t border-zinc-200 p-3" data-testid={`${testId}-fields`}>
      <p className="text-[11px] text-zinc-600">{HINTS[b.type]}</p>
      {b.type === "hero" ? (
        <>
          <Text label="Headline" k="headline" max={160} {...p} />
          <Text label="Message" k="message" max={1200} area {...p} />
        </>
      ) : <Text label="Section title" k="title" max={80} {...p} />}
      {b.type === "next_steps" && (
        <div className="mt-2"><ListEditor items={b.steps || []} onChange={(v) => set("steps", v)} fields={STEP_FIELDS} max={5} testId={`${testId}-steps`} addLabel="Add step" /></div>
      )}
      {b.type === "video" && <Text label="Default video link (YouTube, Vimeo, Wistia or .mp4)" k="url" max={500} {...p} />}
      {b.type === "no_fee" && (
        <div className="mt-2">
          <ListEditor items={(b.items || []).map((t) => ({ t }))} onChange={(v) => set("items", v.map((x) => x.t))}
                      fields={[{ k: "t", placeholder: "e.g. No upfront costs", max: 60 }]} max={6} testId={`${testId}-items`} addLabel="Add point" />
        </div>
      )}
      {b.type === "articles" && (
        <div className="mt-2"><ListEditor items={b.articles || []} onChange={(v) => set("articles", v)} fields={ARTICLE_FIELDS} max={6} testId={`${testId}-articles`} addLabel="Add article" /></div>
      )}
    </div>
  );
}

const str = (v) => (typeof v === "string" ? v.trim() || null : null);
export function cleanBlock(b) {
  const out = { type: b.type, enabled: b.enabled !== false };
  ["title", "headline", "message", "url"].forEach((k) => { if (str(b[k])) out[k] = str(b[k]); });
  if (b.steps?.length) {
    out.steps = b.steps.filter((x) => x.title?.trim())
      .map((x) => ({ title: x.title.trim(), body: (x.body || "").trim(), image_url: str(x.image_url) }));
  }
  if (b.items?.length) out.items = b.items.map((t) => (t || "").trim()).filter(Boolean);
  if (b.articles?.length) {
    out.articles = b.articles.filter((a) => a.title?.trim() && a.url?.trim())
      .map((a) => ({ title: a.title.trim(), url: a.url.trim(), image_url: str(a.image_url) }));
  }
  return out;
}
